import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import type { ReactNode } from "react";
import type { User } from "@/types";
import { authService } from "@modules/auth/services";
import { firebaseGetRedirectResult } from "@services/config/firebase";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import { httpClient } from "@services/http/httpClient";
import { TOKEN_KEY } from "@shared/constants/auth";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Sign in with email and password. Returns error message or null on success. */
  signIn: (email: string, password: string) => Promise<string | null>;
  /** Sign up with email, password, and profile data. Returns error message or null. */
  signUp: (
    email: string,
    password: string,
    profile: {
      fullName: string;
      cedula: string;
      phone: string;
      address: string;
    },
  ) => Promise<string | null>;
  /** Google Sign-In. Returns error message or null on success. */
  signInWithGoogle?: () => Promise<string | null>;
  /** Sign out. */
  logout: () => void;
  /** Reload user data from backend — call after profile update to sync auth context. */
  refreshUser: () => Promise<void>;
  /**
   * @deprecated Use signIn() instead. Kept for backwards-compat with mock mode.
   */
  login: (token: string) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export type { AuthContextValue };

interface Props {
  children: ReactNode;
}

// Inactivity timeout constant — moved outside component to avoid dependency array issues
const INACTIVITY_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days
const LAST_ACTIVITY_KEY = "ticketin_last_activity_at";
const ACTIVITY_THROTTLE = 5000; // Write to localStorage max once per 5 seconds

// Session max age: 15 days — absolute expiry from first login
const SESSION_MAX_AGE = 15 * 24 * 60 * 60 * 1000;
const SESSION_CREATED_AT_KEY = "ticketin_session_created_at";

/**
 * Retrieve the last recorded user activity timestamp from localStorage.
 * Validates that the parsed value is a finite number before returning.
 * @returns Last activity timestamp in milliseconds, or null if not set or invalid.
 */
function getLastActivity(): number | null {
  const value = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Throttled write to localStorage.
 * Only writes if last write was >ACTIVITY_THROTTLE ms ago.
 * Returns true if write occurred, false if throttled.
 */
function setLastActivityThrottled(now: number): boolean {
  const lastActivity = getLastActivity();
  if (!lastActivity || now - lastActivity > ACTIVITY_THROTTLE) {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    return true;
  }
  return false;
}

function clearLastActivity(): void {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

function isSessionExpiredByInactivity(now: number): boolean {
  const lastActivity = getLastActivity();
  if (!lastActivity) return false;
  return now - lastActivity > INACTIVITY_TIMEOUT;
}

function setSessionCreatedAt(): void {
  if (!localStorage.getItem(SESSION_CREATED_AT_KEY)) {
    localStorage.setItem(SESSION_CREATED_AT_KEY, String(Date.now()));
  }
}

function clearSessionCreatedAt(): void {
  localStorage.removeItem(SESSION_CREATED_AT_KEY);
}

function isSessionExpiredByAge(): boolean {
  const value = localStorage.getItem(SESSION_CREATED_AT_KEY);
  if (!value) return false;
  const createdAt = Number(value);
  return Number.isFinite(createdAt) && Date.now() - createdAt > SESSION_MAX_AGE;
}

/**
 * Provides authentication context to the entire app.
 * Configures the legacyHttpClient token getter and loads user on mount.
 * Implements session timeout (30 minutes of inactivity).
 */
export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Tracks whether a Google popup is in flight — prevents signOut during popup focus steal
  const isPopupActiveRef = useRef(false);

  // Wire up both HTTP clients to read the Firebase ID Token from authService
  useEffect(() => {
    legacyHttpClient.setTokenGetter(() => authService.getToken());
    httpClient.setTokenGetter(() => authService.getToken());
  }, []);

  const loadUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setIsLoading(false);
      return;
    }

    const now = Date.now();
    if (isSessionExpiredByAge()) {
      authService.clearToken();
      void authService.signOut();
      setUser(null);
      clearLastActivity();
      clearSessionCreatedAt();
      setIsLoading(false);
      return;
    }

    // Reset in-session inactivity timer on page load — only cross-session
    // inactivity (via sessionCreatedAt) is checked, not time between visits.
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));

    try {
      const res = await authService.getCurrentUser();
      if (res.success) {
        setUser(res.data);
        setLastActivityThrottled(now);
      } else {
        // Token invalid or expired
        authService.clearToken();
        setUser(null);
        clearLastActivity();
      }
    } catch {
      // Network or backend error — do NOT clear the token.
      // The Firebase session may still be valid; clearing the token here would
      // permanently log out the user on any transient backend failure.
      // The user will see an unauthenticated state until the next successful load.
      setUser(null);
      clearLastActivity();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  // Handle Google sign-in redirect result (used in Instagram/Facebook/TikTok WebViews).
  // getRedirectResult returns null on every normal load — only has a value after
  // the user returns from a signInWithRedirect flow.
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await firebaseGetRedirectResult();
        if (!result) return;
        localStorage.setItem(TOKEN_KEY, result.idToken);
        setUser({
          id: result.uid,
          uid: result.uid,
          name: result.displayName ?? "",
          mail: result.email,
          address: null,
          phone: null,
          cedula: null,
          notifications: true,
          image: null,
        });
        setSessionCreatedAt();
        setLastActivityThrottled(Date.now());
      } catch {
        // No redirect pending or error — ignore silently
      }
    };
    void handleRedirectResult(); 
  }, []);

  // Listen for logout from other tabs via localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If TOKEN_KEY was removed in another tab, clear this tab too
      if (e.key === TOKEN_KEY && e.newValue === null) {
        setUser(null);
        clearLastActivity();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Check session expiration when tab regains focus.
  // Skip check if a Google popup is in flight — the popup steals window focus,
  // triggering visibilityState "hidden" → "visible" while auth is resolving.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && user && !isPopupActiveRef.current) {
        const now = Date.now();
        if (isSessionExpiredByAge() || isSessionExpiredByInactivity(now)) {
          void authService.signOut();
          clearLastActivity();
          clearSessionCreatedAt();
          setUser(null);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user]);

  // Inactivity timeout
  useEffect(() => {
    if (!user) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const resetTimeout = () => {
      const now = Date.now();
      setLastActivityThrottled(now);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        clearLastActivity();
        clearSessionCreatedAt();
        void authService.signOut();
        setUser(null);
      }, INACTIVITY_TIMEOUT);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, resetTimeout);
    });

    const now = Date.now();
    const lastActivity = getLastActivity();
    const elapsed = lastActivity ? now - lastActivity : 0;
    const remaining = Math.max(INACTIVITY_TIMEOUT - elapsed, 0);

    if (remaining === 0) {
      clearLastActivity();
      clearSessionCreatedAt();
      void authService.signOut();
      setUser(null);
      return;
    }

    timeoutId = setTimeout(() => {
      clearLastActivity();
      clearSessionCreatedAt();
      void authService.signOut();
      setUser(null);
    }, remaining);

    return () => {
      clearTimeout(timeoutId);
      events.forEach((event) => {
        document.removeEventListener(event, resetTimeout);
      });
    };
  }, [user]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      try {
        const res = await authService.signIn(email, password);
        if (res.success) {
          setUser(res.data);
          setSessionCreatedAt();
          setLastActivityThrottled(Date.now());
          return null;
        }
        // Single, canonical check — realAuthService always returns this exact string
        // for auth/email-not-verified (see realAuthService.ts signIn catch block)
        return res.message ?? "Error al iniciar sesión";
      } catch (error) {
        return error instanceof Error ? error.message : "Error desconocido";
      }
    },
    [],
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      profile: {
        fullName: string;
        cedula: string;
        phone: string;
        address: string;
      },
    ): Promise<string | null> => {
      try {
        const res = await authService.signUp(email, password, profile);
        if (res.success) {
          return null; // ← null = success, modal proceeds to setStep("success")
        }
        return res.message ?? "Error al crear la cuenta";
      } catch (error) {
        return error instanceof Error ? error.message : "Error desconocido";
      }
    },
    [],
  );

  /** @deprecated Use signIn() instead */
  const login = useCallback(
    (token: string) => {
      authService.setToken(token);
      setLastActivityThrottled(Date.now());
      void loadUser();
    },
    [loadUser],
  );

  const logout = useCallback(async () => {
    await authService.signOut();
    clearLastActivity();
    clearSessionCreatedAt();
    setUser(null);
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<string | null> => {
    if (!authService.signInWithGoogle) {
      return "Google Sign-In no disponible";
    }
    isPopupActiveRef.current = true;
    try {
      const res = await authService.signInWithGoogle();
      if (res.success) {
        setUser(res.data);
        setSessionCreatedAt();
        setLastActivityThrottled(Date.now());
        return null;
      }
      return res.message ?? "Error al iniciar sesión con Google";
    } catch (error) {
      return error instanceof Error ? error.message : "Error desconocido";
    } finally {
      isPopupActiveRef.current = false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      signIn,
      signUp,
      signInWithGoogle: authService.signInWithGoogle
        ? signInWithGoogle
        : undefined,
      login,
      logout,
      refreshUser,
    }),
    [user, isLoading, signIn, signUp, signInWithGoogle, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
